/**
 * <infinite-scroll>
 *
 * ページネーションを無限スクロールに置き換えるカスタム要素。
 * 商品一覧 (collection-uniform-grid) から snippets/infinite-scroll.liquid 経由で使う。
 *
 * - 要素が画面下に近づいたら次ページを取り、グリッドの末尾にカードを足す。
 * - 取得は ?section_id= を付けてセクション単位でレンダリングさせる (Dawn と同じ手)。
 * - JS が動かない環境では中の <pagination> がそのまま残るので、通常のページャーとして機能する。
 * - 商品を見て戻ってきたときは、離脱前に読み込んでいたページ数とスクロール位置を復元する。
 * - data-source-grid があるときは別ページ (コレクションページ) から続きを取る。
 *   手元に既にあるカードは重複するのでリンク先で弾く。
 *
 * Markup:
 *   <infinite-scroll
 *     data-grid="product-grid"          追記先グリッドの id
 *     data-items=".pvz-ugrid__cell"     追記する要素のセレクター
 *     data-source-grid="product-grid"   取得先ページで読むグリッドの id (別ページから取る場合)
 *     data-section-id="{{ section.id }}"
 *     data-page="{{ paginate.current_page }}"
 *     data-next-url="{{ paginate.next.url }}">
 *     <div data-infinite-fallback>…ページャー…</div>
 *     <button data-infinite-more>LOAD MORE</button>
 *   </infinite-scroll>
 */
if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      connectedCallback() {
        this.grid = document.getElementById(this.dataset.grid);
        if (!this.grid) return;

        this.itemSelector = this.dataset.items || '.pvz-ugrid__cell';
        this.sectionId = this.dataset.sectionId;
        this.nextUrl = this.dataset.nextUrl || null;
        this.page = parseInt(this.dataset.page, 10) || 1;
        this.loading = false;

        // 別ページから続きを取るモード。取得先のグリッド id が手元と違う。
        this.sourceGrid = this.dataset.sourceGrid || null;
        this.crossPage = Boolean(this.sourceGrid);
        // 取得先セクションの id は 1 回目のレスポンスから拾って、2 回目以降を軽くする
        this.remoteSectionId = null;
        this.seen = new Set();
        if (this.crossPage) this.rememberExisting();

        this.button = this.querySelector('[data-infinite-more]');
        this.fallback = this.querySelector('[data-infinite-fallback]');

        // 次ページが無い状態で描画された場合 (最終ページを直接開いたときなど)。
        // 押しても何も起きないボタンは出さず、素のページャーだけ残す。
        if (!this.nextUrl) {
          if (this.button) this.button.remove();
          return;
        }

        // JS が動く環境では素のページャーを隠し、ボタン + 自動読み込みに切り替える
        if (this.fallback) this.fallback.hidden = true;
        if (this.button) {
          this.button.hidden = false;
          this.button.addEventListener('click', () => this.loadNext());
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
            },
            // 画面に入る手前で取りに行き、スクロールが止まらないようにする
            { rootMargin: '800px 0px' }
          );
          this.observer.observe(this);
        }

        this.storageKey = 'pvz-infinite:' + location.pathname + location.search;
        window.addEventListener('pagehide', () => this.savePosition());
        this.restorePosition();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async loadNext() {
        if (this.loading || !this.nextUrl) return false;

        this.loading = true;
        this.classList.add('is-loading');
        this.setAttribute('aria-busy', 'true');

        try {
          // セクション単位でレンダリングさせると、ヘッダーやフッターを含まない分だけ軽い
          const url = new URL(this.nextUrl, location.origin);
          const sectionId = this.crossPage ? this.remoteSectionId : this.sectionId;
          if (sectionId) url.searchParams.set('section_id', sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error('HTTP ' + response.status);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const sourceGrid = this.sourceGrid || this.dataset.grid;
          const items = doc.querySelectorAll('#' + sourceGrid + ' ' + this.itemSelector);

          const fragment = document.createDocumentFragment();
          items.forEach((item) => {
            // 別ページから取る場合、手元に出ている商品と重なるので弾く
            if (this.crossPage) {
              const key = this.keyOf(item);
              if (key) {
                if (this.seen.has(key)) return;
                this.seen.add(key);
              }
            }
            fragment.appendChild(document.importNode(item, true));
          });
          this.grid.appendChild(fragment);

          // 次ページの有無は、取得した HTML の <infinite-scroll> がそのまま持っている
          const next = doc.querySelector('infinite-scroll');
          if (this.crossPage && next && next.dataset.sectionId) this.remoteSectionId = next.dataset.sectionId;
          this.nextUrl = (next && next.dataset.nextUrl) || null;
          this.page += 1;

          if (!this.nextUrl) {
            this.finish();
            return true;
          }

          // IntersectionObserver は交差状態が「変わったとき」しか発火しない。
          // 重複除去で 1 件も増えなかった場合など、要素が画面内に残ったままだと
          // 次が呼ばれないので、まだ画面の近くにいるなら自分で続けて取りに行く。
          requestAnimationFrame(() => {
            if (this.isNearViewport()) this.loadNext();
          });
          return true;
        } catch (e) {
          // 失敗したら自動読み込みは止め、ボタンだけ残して手動で再試行できるようにする
          if (this.observer) this.observer.disconnect();
          return false;
        } finally {
          this.loading = false;
          this.classList.remove('is-loading');
          this.removeAttribute('aria-busy');
        }
      }

      /** observer の rootMargin と同じ範囲に入っているか */
      isNearViewport() {
        const rect = this.getBoundingClientRect();
        return rect.top < window.innerHeight + 800 && rect.bottom > -800;
      }

      /** カードの識別子。商品リンクの URL を使う。 */
      keyOf(item) {
        const link = item.matches('a[href]') ? item : item.querySelector('a[href]');
        return link ? link.getAttribute('href').split('?')[0] : null;
      }

      /** 既にサーバー側で描画済みのカードを重複判定用に控えておく */
      rememberExisting() {
        this.grid.querySelectorAll(this.itemSelector).forEach((item) => {
          const key = this.keyOf(item);
          if (key) this.seen.add(key);
        });
      }

      finish() {
        if (this.observer) this.observer.disconnect();
        this.hidden = true;
      }

      savePosition() {
        try {
          sessionStorage.setItem(
            this.storageKey,
            JSON.stringify({ pages: this.page, scrollY: Math.round(window.scrollY) })
          );
        } catch (e) {
          /* sessionStorage が使えない環境では復元しない */
        }
      }

      /**
       * 商品ページから戻ってきたときだけ、離脱前のページ数まで読み直して位置を戻す。
       * 直接アクセスや再読み込みでは 1 ページ目から始める。
       */
      async restorePosition() {
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation && navigation.type !== 'back_forward') return;

        let saved = null;
        try {
          saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        } catch (e) {
          return;
        }
        if (!saved || !saved.pages || saved.pages <= this.page) return;

        while (this.nextUrl && this.page < saved.pages) {
          if (!(await this.loadNext())) break;
        }

        if (typeof saved.scrollY === 'number') window.scrollTo(0, saved.scrollY);
      }
    }
  );
}
